'use client';
import React, { useCallback } from 'react';
import { Slider } from '@benzinga/core-ui';
import styled from '@benzinga/themetron';

import { InputGroup } from '../InputGroup';
import { MultiRangeLabels, MultiRange, Range } from '../../entities';

export interface RangeSelectProps {
  label: string;
  onChange: (value: Range) => void;
  selected: Range;
  range: MultiRange;
  rangeLabels?: MultiRangeLabels;
  unit?: string;
  step: number;
}

export const RangeSelectGroup: React.FC<RangeSelectProps> = ({
  label,
  onChange,
  range,
  rangeLabels,
  selected,
  step,
  unit = '$',
}) => {
  const getRangeLabels = useCallback(() => {
    if (rangeLabels) {
      if (rangeLabels.length === 2) {
        return (
          <span>
            {rangeLabels[0]} – {rangeLabels[1]}
          </span>
        );
      } else if (rangeLabels.length > 2) {
        const labels = [...rangeLabels];
        const firstLabel = labels.shift();
        const lastLabel = labels.pop();

        return (
          <>
            <span>{firstLabel}</span>
            <div className="slider-container__between-ranges">
              {labels.map((item, i) => {
                return <span key={i}>{item}</span>;
              })}
            </div>
            <span>{lastLabel}</span>
          </>
        );
      }
    }

    return (
      <span>
        {selected[0]}
        {unit} – {selected[1]}
        {unit}
      </span>
    );
  }, [rangeLabels, selected, unit]);

  return (
    <RangeSelectWrapper className="input-group">
      <label>{label}</label>
      <div className="slider-container">
        <div className="slider-container__label">{getRangeLabels()}</div>
        <Slider
          defaultValue={selected}
          max={range.length > 2 ? range[range.length - 1] : range[1]}
          min={range[0]}
          multi={true}
          onChange={onChange as ((value: number | Range) => void) | undefined}
          range={range}
          step={step}
        />
      </div>
    </RangeSelectWrapper>
  );
};

export const RangeSelectWrapper = styled(InputGroup)`
  .slider-container {
    &__label {
      display: flex;
      justify-content: space-between;
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
      font-size: ${({ theme }) => theme.fontSize.base};
    }

    &__between-ranges {
      display: flex;
      flex-grow: 1;
      justify-content: space-around;
    }
  }
`;
